import type {
  Channel,
  NotificationApplicableScope,
  NotificationBackupStage,
  NotificationPolicyConfigField,
  NotificationPolicyConfigIssue,
  NotificationPolicyConfigurationLimits,
  NotificationPolicyConfigurationResolution,
  NotificationPolicyPatchParseResult,
  NotificationPolicyScope,
  NotificationProviderPolicy,
  NotificationPublishedPolicyBinding,
  NotificationResponsibilitySelector,
  UserRole,
} from '@statura/types';

// @statura/utils keeps no runtime import from @statura/types (CJS consumers resolve
// its unbuilt dist). This mirror is compile-checked against UserRole both ways.
const POLICY_USER_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'CEO',
  'OWNER',
  'OPERATIONS_DIRECTOR',
  'CLINICAL_DIRECTOR',
  'FINANCE_DIRECTOR',
  'QUALITY_DIRECTOR',
  'HR_DIRECTOR',
  'REGION_MANAGER',
  'REGION_COMPLIANCE_OFFICER',
  'REGION_FINANCE_OFFICER',
  'COORDINATOR',
  'TEAM_LEADER',
  'REGISTERED_NURSE',
  'ENROLLED_NURSE',
  'CARE_WORKER',
  'ALLIED_HEALTH',
  'FINANCE_OFFICER',
  'PAYROLL_OFFICER',
  'CLIENT',
  'FAMILY_CARER',
  'CLINICAL_LEAD',
  'AGENCY_CONTRACTOR',
  'FACILITY_MANAGER',
  'QUALITY_OFFICER',
  'COMPLIANCE_OFFICER',
  'CASE_MANAGER',
  'LIFESTYLE_OFFICER',
  'HR_OFFICER',
] as const satisfies readonly UserRole[];
type MissingPolicyRole = Exclude<UserRole, (typeof POLICY_USER_ROLES)[number]>;
const policyRolesExhaustive: [MissingPolicyRole] extends [never] ? true : never = true;
void policyRolesExhaustive;
function isUserRole(value: unknown): value is UserRole {
  return typeof value === 'string' && (POLICY_USER_ROLES as readonly string[]).includes(value);
}

const FIELDS: NotificationPolicyConfigField[] = [
  'primary',
  'supporting',
  'observers',
  'backups',
  'defaultChannels',
  'digest',
  'quietHours',
  'unclaimedMs',
  'approval',
];
const CHANNELS: Channel[] = ['in_app', 'push', 'email', 'sms'];
const SELECTOR_KINDS: NotificationResponsibilitySelector['kind'][] = [
  'SOURCE_OWNER',
  'NAMED',
  'SCOPED_ROLE',
  'DUTY',
  'TEAM',
];
const uuid = (value: unknown): value is string =>
  typeof value === 'string' && /^[a-f0-9]{8}-(?:[a-f0-9]{4}-){3}[a-f0-9]{12}$/i.test(value);
const token = (value: unknown): value is string =>
  typeof value === 'string' && /^[A-Za-z][A-Za-z0-9_.:-]{0,159}$/.test(value);
const time = (value: unknown): value is string =>
  typeof value === 'string' && /^(?:[01][0-9]|2[0-3]):[0-5][0-9]$/.test(value);
const timer = (value: unknown): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value > 0;
const issue = (
  path: string,
  code: NotificationPolicyConfigIssue['code'],
): NotificationPolicyConfigIssue => ({ path, code, basis: 'INTEGRITY' });
class InvalidConfiguration extends Error {
  constructor(readonly detail: NotificationPolicyConfigIssue) {
    super(detail.code);
  }
}
function fail(path: string, code: NotificationPolicyConfigIssue['code']): never {
  throw new InvalidConfiguration(issue(path, code));
}
function object(
  value: unknown,
  path: string,
  keys: readonly string[],
  complete = false,
): Record<string, unknown> {
  if (
    !value ||
    typeof value !== 'object' ||
    Array.isArray(value) ||
    ![Object.prototype, null].includes(Object.getPrototypeOf(value))
  )
    fail(path, 'INVALID_SHAPE');
  const row = value as Record<string, unknown>;
  for (const key of Object.keys(row))
    if (!keys.includes(key)) fail(`${path}.${key}`, 'UNKNOWN_FIELD');
  if (complete && keys.some((key) => !Object.hasOwn(row, key))) fail(path, 'INVALID_SHAPE');
  return row;
}
function list(value: unknown, path: string, max: number): unknown[] {
  if (!Array.isArray(value) || value.length > max) fail(path, 'INVALID_SHAPE');
  for (let index = 0; index < value.length; index++)
    if (!Object.hasOwn(value, index)) fail(path, 'INVALID_SHAPE');
  return value;
}
function selector(value: unknown, path: string): NotificationResponsibilitySelector {
  const raw = object(value, path, ['kind', 'userIds', 'role', 'dutyKey', 'teamKey']);
  if (!Object.hasOwn(raw, 'kind')) fail(path, 'INVALID_SELECTOR');
  switch (raw.kind) {
    case 'SOURCE_OWNER':
      object(value, path, ['kind'], true);
      return { kind: 'SOURCE_OWNER' };
    case 'NAMED': {
      object(value, path, ['kind', 'userIds'], true);
      const ids = list(raw.userIds, `${path}.userIds`, 100);
      if (!ids.length || !ids.every(uuid)) fail(path, 'INVALID_SELECTOR');
      const normalised = ids.map((id) => id.toLowerCase()).sort();
      if (new Set(normalised).size !== ids.length) fail(path, 'INVALID_SELECTOR');
      return { kind: 'NAMED', userIds: normalised };
    }
    case 'SCOPED_ROLE':
      object(value, path, ['kind', 'role'], true);
      if (!isUserRole(raw.role) || raw.role === 'CLIENT' || raw.role === 'FAMILY_CARER')
        fail(path, 'INVALID_SELECTOR');
      return { kind: 'SCOPED_ROLE', role: raw.role };
    case 'DUTY':
      object(value, path, ['kind', 'dutyKey'], true);
      if (!token(raw.dutyKey)) fail(path, 'INVALID_SELECTOR');
      return { kind: 'DUTY', dutyKey: raw.dutyKey };
    case 'TEAM':
      object(value, path, ['kind', 'teamKey'], true);
      if (!token(raw.teamKey)) fail(path, 'INVALID_SELECTOR');
      return { kind: 'TEAM', teamKey: raw.teamKey };
    default:
      return fail(path, 'INVALID_SELECTOR');
  }
}
function selectors(value: unknown, path: string): NotificationResponsibilitySelector[] {
  const rows = list(value, path, 100).map((row, i) => selector(row, `${path}[${i}]`));
  if (new Set(rows.map((row) => JSON.stringify(row))).size !== rows.length)
    fail(path, 'INVALID_SELECTOR');
  return rows;
}
function field(key: NotificationPolicyConfigField, value: unknown): unknown {
  switch (key) {
    case 'primary':
      return value === null ? null : selector(value, key);
    case 'supporting':
    case 'observers':
      return selectors(value, key);
    case 'backups':
      return list(value, key, 16).map((value, i): NotificationBackupStage => {
        const path = `backups[${i}]`,
          row = object(value, path, ['selector', 'trigger', 'afterMs'], true);
        if (row.trigger !== 'UNCLAIMED' && row.trigger !== 'UNRESOLVED')
          fail(path, 'INVALID_SHAPE');
        if (!timer(row.afterMs)) fail(`${path}.afterMs`, 'INVALID_TIMER');
        return {
          selector: selector(row.selector, `${path}.selector`),
          trigger: row.trigger,
          afterMs: row.afterMs,
        };
      });
    case 'defaultChannels': {
      const rows = list(value, key, 4);
      if (
        !rows.every((v): v is Channel => CHANNELS.includes(v as Channel)) ||
        new Set(rows).size !== rows.length
      )
        fail(key, 'INVALID_CHANNEL');
      return [...rows];
    }
    case 'unclaimedMs':
      if (!timer(value)) fail(key, 'INVALID_TIMER');
      return value;
    case 'digest': {
      const row = object(value, key, ['mode', 'at', 'clock'], true);
      if (
        (row.mode !== 'NONE' && row.mode !== 'DAILY') ||
        row.clock !== 'SOURCE_SCOPE' ||
        (row.mode === 'DAILY' ? !time(row.at) : row.at !== null)
      )
        fail(key, 'INVALID_CLOCK');
      return { ...row };
    }
    case 'quietHours': {
      const row = object(value, key, ['enabled', 'start', 'end', 'clock'], true);
      if (
        typeof row.enabled !== 'boolean' ||
        !time(row.start) ||
        !time(row.end) ||
        row.clock !== 'RECIPIENT_DELIVERY' ||
        (row.enabled && row.start === row.end)
      )
        fail(key, 'INVALID_CLOCK');
      return { ...row };
    }
    case 'approval': {
      const row = object(value, key, ['reductionsRequireReview'], true);
      if (typeof row.reductionsRequireReview !== 'boolean') fail(key, 'INVALID_SHAPE');
      return { ...row };
    }
  }
}

/** JSON boundary validation only. It does not establish permission or coverage. */
export function parseNotificationProviderPolicyPatch(
  input: unknown,
): NotificationPolicyPatchParseResult {
  try {
    if (new TextEncoder().encode(JSON.stringify(input)).byteLength > 65536)
      fail('policy', 'SIZE_LIMIT');
    const raw = object(input, 'policy', FIELDS),
      result: Record<string, unknown> = {},
      issues: NotificationPolicyConfigIssue[] = [];
    for (const key of FIELDS)
      if (Object.hasOwn(raw, key)) {
        try {
          result[key] = field(key, raw[key]);
        } catch (error) {
          if (error instanceof InvalidConfiguration) issues.push(error.detail);
          else throw error;
        }
      }
    return issues.length ? { ok: false, issues } : { ok: true, value: result };
  } catch (error) {
    return {
      ok: false,
      issues: [
        error instanceof InvalidConfiguration ? error.detail : issue('policy', 'INVALID_SHAPE'),
      ],
    };
  }
}

/** Decode a complete configuration; sparse drafts use the patch parser. */
export function parseNotificationProviderPolicy(
  input: unknown,
):
  | { ok: true; value: NotificationProviderPolicy }
  | { ok: false; issues: NotificationPolicyConfigIssue[] } {
  const parsed = parseNotificationProviderPolicyPatch(input);
  if (!parsed.ok) return parsed;
  if (!FIELDS.every((key) => Object.hasOwn(parsed.value, key)))
    return { ok: false, issues: [issue('policy', 'INVALID_SHAPE')] };
  return { ok: true, value: parsed.value as NotificationProviderPolicy };
}

/** Static catalogue floors ONLY. Publication also requires fresh actor, scope,
 * qualified coverage, consent, preview/dependency checks and a controlled CAS. */
export function validateNotificationPolicyConfigurationFloors(
  policy: NotificationProviderPolicy,
  limits: NotificationPolicyConfigurationLimits,
): NotificationPolicyConfigIssue[] {
  const parsed = parseNotificationProviderPolicy(policy);
  if (!parsed.ok) return parsed.issues;
  if (!limits || typeof limits !== 'object') return [issue('catalogue', 'INVALID_SHAPE')];
  const issues: NotificationPolicyConfigIssue[] = [];
  const add = (path: string, code: NotificationPolicyConfigIssue['code']) =>
    issues.push(issue(path, code));
  if (limits.catalogueStatus !== 'APPROVED') add('policy', 'CATALOGUE_NOT_APPROVED');
  if (
    !Array.isArray(limits.permittedSelectorKinds) ||
    !Array.isArray(limits.permittedRoles) ||
    !limits.permittedSelectorKinds.every((kind) => SELECTOR_KINDS.includes(kind)) ||
    !limits.permittedRoles.every(isUserRole) ||
    !Array.isArray(limits.allowedChannels) ||
    !limits.allowedChannels.every((channel) => CHANNELS.includes(channel)) ||
    ![
      limits.requiresPrimary,
      limits.requiresBackup,
      limits.digestAllowed,
      limits.quietHoursAllowed,
    ].every((value) => typeof value === 'boolean')
  )
    return [issue('catalogue', 'INVALID_SHAPE')];
  for (const map of [limits.permittedSelectorKindsByPurpose, limits.permittedRolesByPurpose]) {
    if (
      map !== undefined &&
      (!map ||
        typeof map !== 'object' ||
        Array.isArray(map) ||
        Object.keys(map).some(
          (purpose) => !['PRIMARY', 'SUPPORTING', 'OBSERVER'].includes(purpose),
        ) ||
        Object.values(map).some(
          (values) => !Array.isArray(values) || values.some((value) => typeof value !== 'string'),
        ))
    )
      return [issue('catalogue', 'INVALID_SHAPE')];
  }
  if (
    Object.values(limits.permittedSelectorKindsByPurpose ?? {}).some(
      (values) => !values.every((kind) => SELECTOR_KINDS.includes(kind)),
    ) ||
    Object.values(limits.permittedRolesByPurpose ?? {}).some((values) => !values.every(isUserRole))
  )
    return [issue('catalogue', 'INVALID_SHAPE')];
  if (!timer(limits.maxUnclaimedMs) || !timer(limits.maxUnresolvedMs))
    return [issue('catalogue', 'INVALID_TIMER')];
  if (limits.requiresPrimary && !policy.primary) add('primary', 'PRIMARY_REQUIRED');
  if (policy.primary?.kind === 'NAMED' && policy.primary.userIds.length !== 1)
    add('primary', 'PRIMARY_AMBIGUOUS');
  if (
    limits.requiresBackup &&
    !policy.backups.some(
      (backup) => backup.trigger === 'UNCLAIMED' && backup.afterMs <= policy.unclaimedMs,
    )
  )
    add('backups', 'BACKUP_REQUIRED');
  if (policy.unclaimedMs > limits.maxUnclaimedMs) add('unclaimedMs', 'TIMING_FLOOR');
  if (policy.defaultChannels.some((channel) => !limits.allowedChannels.includes(channel)))
    add('defaultChannels', 'INVALID_CHANNEL');
  if (policy.digest.mode !== 'NONE' && !limits.digestAllowed) add('digest', 'DIGEST_NOT_PERMITTED');
  if (policy.quietHours.enabled && !limits.quietHoursAllowed)
    add('quietHours', 'QUIET_HOURS_NOT_PERMITTED');
  const checkSelector = (
    selected: NotificationResponsibilitySelector,
    path: string,
    purpose: 'PRIMARY' | 'SUPPORTING' | 'OBSERVER',
  ) => {
    const kinds =
      limits.permittedSelectorKindsByPurpose?.[purpose] ?? limits.permittedSelectorKinds;
    const roles = limits.permittedRolesByPurpose?.[purpose] ?? limits.permittedRoles;
    if (
      !kinds.includes(selected.kind) ||
      (selected.kind === 'SCOPED_ROLE' && !roles.includes(selected.role))
    )
      add(path, 'SELECTOR_NOT_PERMITTED');
    if (
      purpose === 'PRIMARY' &&
      selected.kind === 'NAMED' &&
      selected.userIds.length !== 1 &&
      path !== 'primary'
    )
      add(path, 'PRIMARY_AMBIGUOUS');
  };
  if (policy.primary) checkSelector(policy.primary, 'primary', 'PRIMARY');
  policy.supporting.forEach((selected, i) =>
    checkSelector(selected, `supporting[${i}]`, 'SUPPORTING'),
  );
  policy.observers.forEach((selected, i) => checkSelector(selected, `observers[${i}]`, 'OBSERVER'));
  const previous: Partial<Record<NotificationBackupStage['trigger'], number>> = {};
  policy.backups.forEach((backup, i) => {
    checkSelector(backup.selector, `backups[${i}].selector`, 'PRIMARY');
    const maximum = backup.trigger === 'UNCLAIMED' ? limits.maxUnclaimedMs : limits.maxUnresolvedMs;
    if (backup.afterMs > maximum || backup.afterMs <= (previous[backup.trigger] ?? 0))
      add(`backups[${i}].afterMs`, 'TIMING_FLOOR');
    previous[backup.trigger] = backup.afterMs;
  });
  return issues;
}

export function notificationPolicyScopeKey(scope: NotificationPolicyScope): string {
  if (
    !scope ||
    !['TENANT', 'REGION', 'FACILITY', 'SERVICE_AREA'].includes(scope.kind) ||
    (scope.kind === 'TENANT' ? scope.id !== null : !uuid(scope.id))
  )
    throw new Error('Invalid notification policy scope');
  return JSON.stringify([scope.kind, scope.id?.toLowerCase() ?? null]);
}

/** Field-wise inheritance over the canonical applicable-scope graph, not a
 * guessed region/facility tree. Incomparable overrides NEVER union recipients. */
export function resolveNotificationPolicyConfiguration(input: {
  tenantId: string;
  familyKey: string;
  defaults: NotificationProviderPolicy;
  scopes: NotificationApplicableScope[];
  bindings: NotificationPublishedPolicyBinding[];
}): NotificationPolicyConfigurationResolution {
  const conflict = (
    code: 'INVALID_SCOPE_GRAPH' | 'INVALID_POLICY' | 'REVISION_CONFLICT' | 'AMBIGUOUS_SCOPE',
    fields: NotificationPolicyConfigField[] = [],
  ): NotificationPolicyConfigurationResolution => ({ kind: 'CONFLICT', code, fields });
  try {
    const defaults = parseNotificationProviderPolicyPatch(input.defaults);
    if (
      !uuid(input.tenantId) ||
      !token(input.familyKey) ||
      !defaults.ok ||
      !FIELDS.every((key) => Object.hasOwn(defaults.value, key))
    )
      return conflict('INVALID_POLICY');
    const scopes = new Map(input.scopes.map((row) => [notificationPolicyScopeKey(row.scope), row]));
    if (
      !scopes.size ||
      scopes.size !== input.scopes.length ||
      !scopes.has(notificationPolicyScopeKey({ kind: 'TENANT', id: null }))
    )
      return conflict('INVALID_SCOPE_GRAPH');
    const ancestors = new Map<string, Set<string>>();
    const visiting = new Set<string>();
    const walk = (key: string): Set<string> => {
      if (visiting.has(key)) throw new Error('Scope graph cycle');
      const known = ancestors.get(key);
      if (known) return known;
      const row = scopes.get(key);
      if (!row) throw new Error('Missing scope parent');
      visiting.add(key);
      const result = new Set<string>();
      const directParents = new Set<string>();
      for (const parent of row.parents) {
        const p = notificationPolicyScopeKey(parent);
        if (directParents.has(p)) throw new Error('Duplicate scope parent');
        directParents.add(p);
        result.add(p);
        for (const ancestor of walk(p)) result.add(ancestor);
      }
      visiting.delete(key);
      ancestors.set(key, result);
      return result;
    };
    for (const [key, row] of scopes) {
      const parents = walk(key);
      if (
        row.scope.kind === 'TENANT'
          ? parents.size !== 0
          : !parents.has(notificationPolicyScopeKey({ kind: 'TENANT', id: null }))
      )
        return conflict('INVALID_SCOPE_GRAPH');
    }
    const seen = new Set<string>(),
      ids = new Set<string>();
    const bindings = [];
    for (const binding of input.bindings) {
      const key = notificationPolicyScopeKey(binding.scope),
        parsed = parseNotificationProviderPolicyPatch(binding.patch);
      if (
        !uuid(binding.tenantId) ||
        binding.tenantId.toLowerCase() !== input.tenantId.toLowerCase() ||
        binding.familyKey !== input.familyKey ||
        !scopes.has(key) ||
        !uuid(binding.id) ||
        !parsed.ok
      )
        return conflict('INVALID_POLICY');
      if (
        !/^[1-9][0-9]{0,18}$/.test(binding.revision) ||
        BigInt(binding.revision) > 9223372036854775807n ||
        seen.has(key) ||
        ids.has(binding.id.toLowerCase())
      )
        return conflict('REVISION_CONFLICT');
      seen.add(key);
      ids.add(binding.id.toLowerCase());
      bindings.push({ ...binding, key, patch: parsed.value });
    }
    const result = { ...defaults.value } as NotificationProviderPolicy;
    const provenance = {} as Extract<
      NotificationPolicyConfigurationResolution,
      { kind: 'RESOLVED' }
    >['provenance'];
    const ambiguous: NotificationPolicyConfigField[] = [];
    const nonempty = bindings.filter((binding) => Object.keys(binding.patch).length > 0);
    const mostSpecificBindings = nonempty.filter(
      (binding) =>
        !nonempty.some((other) => other !== binding && ancestors.get(other.key)?.has(binding.key)),
    );
    if (mostSpecificBindings.length > 1)
      return conflict(
        'AMBIGUOUS_SCOPE',
        FIELDS.filter((field) =>
          mostSpecificBindings.some((binding) => Object.hasOwn(binding.patch, field)),
        ),
      );
    for (const field of FIELDS) {
      const matches = bindings.filter((binding) => Object.hasOwn(binding.patch, field));
      const mostSpecific = matches.filter(
        (binding) =>
          !matches.some((other) => other !== binding && ancestors.get(other.key)?.has(binding.key)),
      );
      if (mostSpecific.length > 1) {
        ambiguous.push(field);
        continue;
      }
      const selected = mostSpecific[0];
      if (selected) Object.assign(result, { [field]: selected.patch[field] });
      provenance[field] = {
        scope: selected ? { ...selected.scope } : null,
        revisionId: selected?.id ?? null,
        revision: selected?.revision ?? null,
      };
    }
    if (ambiguous.length) return conflict('AMBIGUOUS_SCOPE', ambiguous);
    return {
      kind: 'RESOLVED',
      policy: result,
      provenance,
      revisionVector: bindings
        .map((binding) => ({ id: binding.id, revision: binding.revision }))
        .sort((a, b) => a.id.localeCompare(b.id)),
    };
  } catch {
    return conflict('INVALID_SCOPE_GRAPH');
  }
}
